import { Injectable, EventEmitter, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { QUALIFICATION_CODES } from '../../constants/qualification-code';
import { Constants as CreateSurveyConstants } from '../../constants/create-survey.constants';
import { SurveyStatus } from '../../utils/survey-status';
import { EnvironmentConfig } from '../../interfaces/environment.interface';
import {
  GetCategoryResponse,
  GetCountriesResponse,
  EachCountry,
  CurrencyExchangeRateResponse,
} from '../../interfaces/create-surveys.interface';
import { SurveyStatusInterface } from '../../interfaces/survey.interface';
import { AuthService } from '../auth/auth.service';
import { Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  private readonly _baseUrl = this._env.environment.pureSpectrum.url;
  public isServiceBuyerLoggedIn: boolean = false;
  invokeChangeSurveyStatus = new EventEmitter();

  private _subscription: Subscription = new Subscription();
  constructor(
    private _http: HttpClient,
    private _auth: AuthService,
    @Inject('ENV') private _env: EnvironmentConfig
  ) {}

  getCategories() {
    return this._http.get<GetCategoryResponse>(`${this._baseUrl}/samples`);
  }

  getCountries(addAllOption: boolean = false) {
    return this._http
      .get<GetCountriesResponse>(`${this._baseUrl}/countries`)
      .pipe(
        map((response) => {
          const topCountries: EachCountry[] = []; //contains US and CA country data
          const remainCountries: EachCountry[] = [];
          response.countries.values.forEach((eachCountry) => {
            if (
              eachCountry.short_Code === 'US' ||
              eachCountry.short_Code === 'CA'
            ) {
              topCountries.push(eachCountry);
            } else {
              remainCountries.push(eachCountry);
            }
          });

          topCountries.sort((a: EachCountry, b: EachCountry) =>
            a.short_Code < b.short_Code ? 1 : -1
          );
          remainCountries.sort((a: EachCountry, b: EachCountry) =>
            a.name > b.name ? 1 : -1
          );
          response.countries.values = [...topCountries, ...remainCountries];
          if (addAllOption) {
            response.countries.values.unshift({
              name: 'ALL',
              id: 0,
              short_Code: '',
              pl_code: '',
              lang: [],
              orderId: 0,
            });
          }
          response.countries.values = response.countries.values.map(
            (country, index) => {
              return {
                ...country,
                orderId: index,
              };
            }
          );
          return response.countries.values;
        })
      );
  }

  getRandomString(length: number = 15) {
    const randomChars =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += randomChars.charAt(
        Math.floor(Math.random() * randomChars.length)
      );
    }
    return result;
  }

  getUnit(qualificationCode: number) {
    if (qualificationCode === QUALIFICATION_CODES.INCOME) {
      return CreateSurveyConstants.UNITS.INCOME;
    }
    return CreateSurveyConstants.UNITS.AGE.YEAR;
  }

  public getSurveyStatus(surveyType: string) {
    switch (surveyType) {
      case 'Live':
        return SurveyStatus.Live;
      case 'Paused':
        return SurveyStatus.Paused;
      case 'Closed':
        return SurveyStatus.Closed;
      case 'Invoice':
        return SurveyStatus.Invoice;
      default:
        return SurveyStatus.Draft;
    }
  }

  public onChangeSurveyStatus(survey: SurveyStatusInterface) {
    this.invokeChangeSurveyStatus.emit(survey);
  }

  public checkIfServiceBuyer() {
    const authServiceSubscription =
      this._auth.loggedInAsServiceBuyer$.subscribe((isLoggedIn) => {
        if (isLoggedIn) {
          this.isServiceBuyerLoggedIn = true;
        }
      });
    this._subscription.add(authServiceSubscription);
    return this.isServiceBuyerLoggedIn;
  }

  public formatDate(date: Date) {
    let d = new Date(date),
      month = '' + (d.getMonth() + 1),
      day = '' + d.getDate(),
      year = d.getFullYear();

    if (month.length < 2) {
      month = '0' + month;
    }
    if (day.length < 2) {
      day = '0' + day;
    }
    return [year, month, day].join('');
  }

  getCurrencyExchangeRate(fx: number) {
    return this._http.get<CurrencyExchangeRateResponse>(
      `${this._baseUrl}/international_currency/${fx}?UI=1`
    );
  }

  public localToUSD(
    value: number,
    exchangeRate: number,
    currencyExchangeRate: number
  ) {
    const AEX = value / exchangeRate;
    const exchangedValue = String(AEX - AEX * currencyExchangeRate);
    return Number(parseFloat(exchangedValue).toFixed(2));
  }

  public calculateClicks(numberOfCompletes: number, incidence: number) {
    return Math.round((numberOfCompletes * 100) / incidence);
  }
}
